import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useGetGameState, getGetGameStateQueryKey } from "@workspace/api-client-react";
import { resolveImageUrl } from "@/lib/api";
import { SnowField } from "./SnowField";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

export function MomentOverlay() {
  const reducedMotion = usePrefersReducedMotion();
  const game = useGetGameState({
    query: { queryKey: getGetGameStateQueryKey(), refetchInterval: 3000 },
  });
  const moment = game.data?.currentMoment;
  const active = !!moment?.active;
  const [dismissedAt, setDismissedAt] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("jingle.moment.dismissed") ?? null;
  });
  const startedAt = moment?.startedAt ?? null;
  const dismissed = !!startedAt && dismissedAt === startedAt;
  function dismiss() {
    if (!startedAt) return;
    setDismissedAt(startedAt);
    try {
      window.localStorage.setItem("jingle.moment.dismissed", startedAt);
    } catch {}
  }
  // Clear dismissal once the underlying moment changes.
  useEffect(() => {
    if (startedAt && dismissedAt && dismissedAt !== startedAt) {
      setDismissedAt(null);
    }
  }, [startedAt, dismissedAt]);

  if (!active || dismissed) return null;
  const isSleigh = moment?.type === "sleigh";
  const title =
    moment?.title ?? (isSleigh ? "The sleigh is here." : "A magical moment");
  const body =
    moment?.body ??
    "Look up — the Christmas Spirit Map is bright tonight because of you.";
  const img = resolveImageUrl(moment?.imageUrl ?? "");

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% 30%, hsla(40, 80%, 60%, 0.35), hsla(163, 60%, 8%, 0.95) 60%, hsl(163, 60%, 6%) 100%)",
        backdropFilter: "blur(8px)",
        animation: reducedMotion ? undefined : "fadeIn 0.6s ease-out both",
      }}
    >
      <SnowField flakeCount={reducedMotion ? 0 : 120} />
      <div className="relative z-10 mx-auto max-w-2xl px-6 text-center text-white">
        {img ? (
          <img
            src={img}
            alt=""
            className="mx-auto mb-8 h-48 w-48 rounded-full object-cover shadow-2xl ring-4 ring-amber-400/60"
            loading="eager"
            decoding="async"
          />
        ) : (
          <div
            className="mx-auto mb-8 h-48 w-48 rounded-full"
            style={{
              background:
                "radial-gradient(circle, hsl(40, 90%, 70%) 0%, hsl(40, 80%, 50%) 50%, hsl(350, 60%, 30%) 100%)",
              boxShadow: "0 0 80px hsl(40, 90%, 60%)",
              animation: reducedMotion ? undefined : "float 4s ease-in-out infinite",
            }}
          />
        )}
        <h1
          className="font-serif text-5xl font-semibold tracking-tight md:text-6xl"
          style={{ textShadow: "0 4px 32px rgba(0,0,0,0.6)" }}
        >
          {title}
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-amber-100/90">
          {body}
        </p>
        {isSleigh && moment?.durationSeconds ? (
          <p className="mt-8 font-mono text-sm uppercase tracking-[0.3em] text-amber-200/70">
            Hold this moment.
          </p>
        ) : null}
        <button
          onClick={dismiss}
          className="mt-10 rounded-full border border-amber-300/40 px-6 py-2 text-sm uppercase tracking-widest text-amber-100/80 transition hover:bg-amber-300/10"
          data-testid="button-dismiss-moment"
        >
          Close
        </button>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>,
    document.body,
  );
}
